/**
 * Controlador de Configuración de Formularios de Eventos.
 *
 * Este módulo gestiona toda la lógica de negocio para la configuración
 * administrativa de formularios de registro de eventos, incluyendo la
 * gestión de fechas, campos personalizados y validaciones.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getEventFormByDataSource,
  updateEventRegistrationForm,
} from "../services/eventRegistrationService";
import EventFormDetailPage from "../modules/EventFormDetailPage";
import EventConfigModal from "../modules/EventConfigModal";
import CustomFieldModal from "../modules/CustomFieldModal";
import SectionModal from "../modules/SectionModal";
import { FieldDelete } from "../modules/FieldDelete";
import MDAlert from "components/Alert";
import usePermission from "hooks/usePermission";

/**
 * Controlador principal de configuración de formularios de eventos.
 *
 * Este componente maneja toda la lógica administrativa para configurar
 * formularios de registro de eventos, incluyendo fechas de vigencia,
 * campos personalizados, validaciones y gestión de modales.
 *
 * @returns {JSX.Element} Componente con la página de detalle y modales de configuración.
 */
export default function EventFormConfigController() {
  const { dataSourceId } = useParams();

  // Permisos
  const canEditEventForm = usePermission("EventRegistrationForm.editByDataSource");

  // Estados de alertas
  const [isAlert, setIsAlert] = useState(false);
  const [typeAlert, setTypeAlert] = useState("success");
  const [message, setMessage] = useState("");

  // Estados de modales
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  // Estados de carga y envío
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Estados de datos
  const [formData, setFormData] = useState(null);
  const [editedData, setEditedData] = useState({
    form_start_date: "",
    form_end_date: "",
    card_effective_date: "",
    card_expiration_date: "",
    is_active: true,
    max_registrations: "",
    custom_fields: [],
    template: "default",
  });

  // Estados para gestión de campos personalizados
  const [editingField, setEditingField] = useState(null);
  const [editingFieldSectionId, setEditingFieldSectionId] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    label: "",
    type: "alphabetic",
    required: false,
    options: [],
  });

  // Estados para gestión de secciones
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionName, setSectionName] = useState("");

  // Estados para Dialog de confirmación de eliminación
  const [infoDelete, setInfoDelete] = useState({});
  const [deleteType, setDeleteType] = useState("field"); // "field" o "section"

  /**
   * Formatea una fecha del backend para mostrarla en el input datetime-local.
   *
   * Este método convierte fechas en formato ISO o SQL a formato compatible
   * con inputs HTML5 de tipo datetime-local (YYYY-MM-DDTHH:MM).
   *
   * @param {string} dateString - Fecha en formato ISO o SQL del backend.
   * @returns {string} Fecha formateada como "YYYY-MM-DDTHH:MM" o cadena vacía.
   */
  const formatDateForInput = (dateString) => {
    if (!dateString || dateString === null) return "";

    // Si la fecha viene en formato ISO con Z (UTC), convertir a zona horaria local
    // Ejemplo: "2025-11-25T19:00:00.000000Z" → "2025-11-25T14:00" (hora local)
    if (dateString.includes("Z") || dateString.includes("+")) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Si viene en formato "2025-11-25 14:00:00", convertir a "2025-11-25T14:00"
    const cleanDate = dateString.trim();
    if (cleanDate.length >= 16) {
      return cleanDate.substring(0, 16).replace(" ", "T");
    }
    return cleanDate;
  };

  /**
   * Formatea una fecha del input para enviarla al backend.
   *
   * Este método convierte fechas en formato datetime-local (YYYY-MM-DDTHH:MM)
   * al formato esperado por el backend (YYYY-MM-DD HH:MM:SS).
   *
   * @param {string} dateString - Fecha en formato "YYYY-MM-DDTHH:MM" del input.
   * @returns {string|null} Fecha formateada como "YYYY-MM-DD HH:MM:SS" o null si está vacía.
   */
  const formatDateForApi = (dateString) => {
    if (!dateString || dateString === "") return null;

    // Convertir de "2025-11-25T14:00" a "2025-11-25 14:00:00"
    // El backend espera formato: "YYYY-MM-DD HH:MM:SS"
    const formatted = dateString.replace("T", " ");
    return formatted.length === 16 ? formatted + ":00" : formatted;
  };

  /**
   * Carga los datos del formulario desde el backend.
   *
   * Este método obtiene la configuración completa del formulario de registro
   * asociado a un data source, incluyendo fechas, campos personalizados y
   * guarda el nombre del evento en sessionStorage para las migas de pan.
   *
   * @returns {Promise<Object>} Objeto con success y message del resultado.
   */
  const loadFormData = async () => {
    if (!dataSourceId) return;

    setLoading(true);
    try {
      const result = await getEventFormByDataSource(dataSourceId);

      if (result.status === true && result.data) {
        // Guardar el nombre del evento en sessionStorage para las migas de pan
        if (result.data?.data_source?.source_name) {
          sessionStorage.setItem("eventFormName", result.data.data_source.source_name);
        }

        setFormData(result.data);
        setEditedData({
          form_start_date: formatDateForInput(result.data.form_start_date) || "",
          form_end_date: formatDateForInput(result.data.form_end_date) || "",
          card_effective_date: formatDateForInput(result.data.card_effective_date) || "",
          card_expiration_date: formatDateForInput(result.data.card_expiration_date) || "",
          is_active: result.data.is_active ?? false,
          max_registrations: result.data.max_registrations || "",
          custom_fields: result.data.custom_fields || [],
          template: result.data.template || "default",
        });

        return { success: true };
      } else {
        return {
          success: false,
          message: result.message || "No se pudo cargar el formulario",
        };
      }
    } catch (err) {
      console.error("Error al cargar formulario:", err);
      return {
        success: false,
        message: "Error al cargar el formulario",
      };
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando se monta el componente
  useEffect(() => {
    if (dataSourceId) {
      loadFormData();
    }
  }, [dataSourceId]);

  /**
   * Maneja el cambio de valor de un campo del formulario.
   *
   * Este método actualiza el estado del formulario con el nuevo valor
   * del campo especificado.
   *
   * @param {string} field - Nombre del campo a actualizar.
   * @param {any} value - Nuevo valor del campo.
   * @returns {void} No retorna valor, actualiza el estado.
   */
  const handleFieldChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
  };

  /**
   * Valida los datos del formulario antes de guardar.
   *
   * Este método verifica la coherencia de las fechas del formulario y del carnet,
   * así como que la cantidad máxima de registros sea un número positivo.
   *
   * @returns {Object} Objeto con isValid (boolean) y errors (object) con mensajes de error.
   */
  const validateForm = () => {
    const errors = {};

    // Validar que las fechas de inicio y fin del formulario sean coherentes
    if (editedData.form_start_date && editedData.form_end_date) {
      const startDate = new Date(editedData.form_start_date);
      const endDate = new Date(editedData.form_end_date);
      if (startDate >= endDate) {
        errors.form_dates = "La fecha de inicio debe ser anterior a la fecha de cierre";
      }
    }

    // Validar que las fechas del carnet sean coherentes
    if (editedData.card_effective_date && editedData.card_expiration_date) {
      const effectiveDate = new Date(editedData.card_effective_date);
      const expirationDate = new Date(editedData.card_expiration_date);
      if (effectiveDate >= expirationDate) {
        errors.card_dates =
          "La fecha de activación del carnet debe ser anterior a la fecha de expiración";
      }
    }

    // Validar que max_registrations sea un número positivo si se proporciona
    if (editedData.max_registrations && parseInt(editedData.max_registrations) <= 0) {
      errors.max_registrations = "La cantidad máxima de registros debe ser mayor a 0";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  /**
   * Guarda los cambios del formulario en el backend.
   *
   * Este método valida los datos, los formatea correctamente y los envía
   * al servidor para actualizar la configuración del formulario de registro.
   *
   * @returns {Promise<Object>} Objeto con success (boolean) y message (string).
   */
  const handleSubmit = async () => {
    // Validar antes de enviar
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(". ");
      return {
        success: false,
        message: errorMessages,
      };
    }

    setSaving(true);

    try {
      const dataToSend = {
        form_start_date: formatDateForApi(editedData.form_start_date),
        form_end_date: formatDateForApi(editedData.form_end_date),
        card_effective_date: formatDateForApi(editedData.card_effective_date),
        card_expiration_date: formatDateForApi(editedData.card_expiration_date),
        is_active: editedData.is_active,
        max_registrations: editedData.max_registrations
          ? parseInt(editedData.max_registrations)
          : null,
        custom_fields: editedData.custom_fields || [],
        template: editedData.template || "default",
      };

      const result = await updateEventRegistrationForm(dataSourceId, dataToSend);

      if (result.status !== false) {
        return {
          success: true,
          message: "Formulario actualizado correctamente",
        };
      } else {
        return {
          success: false,
          message: result.message || "Error al actualizar el formulario",
        };
      }
    } catch (err) {
      console.error("Error al guardar cambios:", err);
      return {
        success: false,
        message: "Error al guardar los cambios",
      };
    } finally {
      setSaving(false);
    }
  };

  /**
   * Copia el link público del formulario al portapapeles.
   *
   * Este método utiliza la API del navegador para copiar el texto al
   * portapapeles del sistema.
   *
   * @param {string} text - Texto a copiar (URL pública del formulario).
   * @returns {Object} Objeto con success (boolean) y message (string).
   */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    return {
      success: true,
      message: "Link copiado al portapapeles",
    };
  };

  /**
   * Genera la URL pública del formulario de registro.
   *
   * Este método construye la URL completa del formulario público usando
   * el slug del formulario o la URL proporcionada por el backend.
   *
   * @returns {string} URL pública completa del formulario o cadena vacía.
   */
  const getPublicUrl = () => {
    if (!formData) return "";
    return (
      formData.public_url ||
      (formData.slug ? `${window.location.origin}/registro/${formData.slug}` : "")
    );
  };

  /**
   * Maneja los cambios en el array de campos personalizados.
   *
   * Este método actualiza el estado con un nuevo array de campos
   * personalizados modificados.
   *
   * @param {Array} updatedFields - Array actualizado de campos personalizados.
   * @returns {void} No retorna valor, actualiza el estado.
   */
  const handleCustomFieldsChange = (updatedFields) => {
    setEditedData({
      ...editedData,
      custom_fields: updatedFields,
    });
  };

  // ========== GESTIÓN DE SECCIONES ==========

  /**
   * Abre el modal para crear una nueva sección.
   *
   * @returns {void} No retorna valor, actualiza estados y abre modal.
   */
  const handleCreateSection = () => {
    setEditingSection(null);
    setSectionName("");
    setSectionModalOpen(true);
  };

  /**
   * Abre el modal para editar una sección existente.
   *
   * @param {Object} section - Sección a editar.
   * @returns {void} No retorna valor, actualiza estados y abre modal.
   */
  const handleEditSection = (section) => {
    setEditingSection(section);
    setSectionName(section.name);
    setSectionModalOpen(true);
  };

  /**
   * Guarda una sección (crear o editar).
   *
   * @returns {Object} Objeto con success, needsSave y updatedFields.
   */
  const handleSaveSection = () => {
    if (!sectionName || !sectionName.trim()) {
      setMessage("El nombre de la sección es obligatorio");
      setTypeAlert("error");
      setIsAlert(true);
      return { success: false };
    }

    let updatedFields;
    if (editingSection) {
      updatedFields = (editedData.custom_fields || []).map((s) => {
        if (s.id === editingSection.id) {
          return { ...s, name: sectionName.trim() };
        }
        return s;
      });
    } else {
      const newSection = {
        id: `section_${Date.now()}`,
        name: sectionName.trim(),
        fields: [],
      };
      updatedFields = [...(editedData.custom_fields || []), newSection];
    }

    handleCustomFieldsChange(updatedFields);
    setSectionModalOpen(false);

    return {
      success: true,
      needsSave: true,
      updatedFields,
      action: editingSection ? "editada" : "creada",
    };
  };

  /**
   * Elimina una sección y todos sus campos.
   *
   * @param {Object} section - Sección a eliminar.
   * @returns {Object} Objeto con success, needsSave y updatedFields.
   */
  const handleDeleteSection = (section) => {
    const updatedFields = (editedData.custom_fields || []).filter((s) => s.id !== section.id);
    handleCustomFieldsChange(updatedFields);
    return {
      success: true,
      needsSave: true,
      deletedField: section.name,
      updatedFields,
    };
  };

  // ========== GESTIÓN DE CAMPOS PERSONALIZADOS ==========

  /**
   * Abre el modal para crear un nuevo campo personalizado dentro de una sección.
   *
   * @param {string} sectionId - ID de la sección donde se creará el campo.
   * @returns {void} No retorna valor, actualiza estados y abre modal.
   */
  const handleCreateField = (sectionId) => {
    setEditingField(null);
    setEditingFieldSectionId(sectionId);
    setFieldForm({
      label: "",
      type: "",
      required: false,
      options: [],
    });
    setFieldModalOpen(true);
  };

  /**
   * Abre el modal para editar un campo personalizado existente.
   *
   * @param {Object} field - Objeto con los datos del campo a editar.
   * @param {string} sectionId - ID de la sección que contiene el campo.
   * @returns {void} No retorna valor, actualiza estados y abre modal.
   */
  const handleEditField = (field, sectionId) => {
    setEditingField(field);
    setEditingFieldSectionId(sectionId);
    setFieldForm({
      label: field.label,
      type: field.type,
      required: field.required,
      options: field.options || [],
    });
    setFieldModalOpen(true);
  };

  /**
   * Maneja cambios en el formulario de campo personalizado.
   *
   * Este método actualiza el estado del formulario de campo cuando
   * el usuario modifica algún valor.
   *
   * @param {string} field - Nombre del campo a actualizar.
   * @param {any} value - Nuevo valor del campo.
   * @returns {void} No retorna valor, actualiza el estado.
   */
  const handleFieldFormChange = (field, value) => {
    setFieldForm((prev) => {
      const newForm = {
        ...prev,
        [field]: value,
      };
      return newForm;
    });
  };

  /**
   * Cierra el modal de campo personalizado.
   *
   * Este método cierra el modal y resetea el estado de edición del campo.
   *
   * @returns {void} No retorna valor, actualiza estados.
   */
  const handleCloseFieldModal = () => {
    setFieldModalOpen(false);
    setEditingField(null);
  };

  /**
   * Guarda un campo personalizado (crear o editar) dentro de una sección.
   *
   * @returns {Object} Objeto con success, needsSave, updatedFields y action.
   */
  const handleSaveField = () => {
    // Validaciones
    if (!fieldForm.label || !fieldForm.label.trim()) {
      setMessage("El nombre del campo es obligatorio");
      setTypeAlert("error");
      setIsAlert(true);
      return { success: false, message: "El nombre del campo es obligatorio" };
    }

    if (!fieldForm.type || fieldForm.type === "") {
      setMessage("Debe seleccionar un tipo de campo");
      setTypeAlert("error");
      setIsAlert(true);
      return { success: false, message: "Debe seleccionar un tipo de campo" };
    }

    // Validar opciones si es tipo select
    if (fieldForm.type === "select") {
      const hasOptions =
        fieldForm.options &&
        ((typeof fieldForm.options === "string" && fieldForm.options.trim() !== "") ||
          (Array.isArray(fieldForm.options) && fieldForm.options.length > 0));

      if (!hasOptions) {
        setMessage("Debe ingresar al menos una opción para el campo de tipo Lista de Opciones");
        setTypeAlert("error");
        setIsAlert(true);
        return {
          success: false,
          message: "Debe ingresar al menos una opción para el campo de tipo Lista de Opciones",
        };
      }
    }

    // Validar opciones si es tipo checklist
    if (fieldForm.type === "checklist") {
      const hasOptions =
        fieldForm.options &&
        ((typeof fieldForm.options === "string" && fieldForm.options.trim() !== "") ||
          (Array.isArray(fieldForm.options) && fieldForm.options.length > 0));

      if (!hasOptions) {
        setMessage("Debe ingresar al menos una opción para el campo de tipo Checklist");
        setTypeAlert("error");
        setIsAlert(true);
        return {
          success: false,
          message: "Debe ingresar al menos una opción para el campo de tipo Checklist",
        };
      }
    }

    // Procesar opciones si es tipo select o checklist
    let processedFieldForm = { ...fieldForm };
    if (
      (fieldForm.type === "select" || fieldForm.type === "checklist") &&
      typeof fieldForm.options === "string"
    ) {
      processedFieldForm.options = fieldForm.options
        .split(",")
        .map((opt) => opt.trim())
        .filter((opt) => opt);
    }

    let updatedFields;
    if (editingField) {
      if (editingFieldSectionId) {
        // Editar campo existente dentro de su sección
        updatedFields = (editedData.custom_fields || []).map((item) => {
          if (item.id === editingFieldSectionId && item.fields) {
            return {
              ...item,
              fields: item.fields.map((f) => {
                if (f.id === editingField.id) {
                  return { ...f, ...processedFieldForm };
                }
                return f;
              }),
            };
          }
          return item;
        });
      } else {
        // Editar campo suelto (legacy)
        updatedFields = (editedData.custom_fields || []).map((item) => {
          if (item.id === editingField.id && !item.fields) {
            return { ...item, ...processedFieldForm };
          }
          return item;
        });
      }
    } else {
      const newField = {
        id: `field_${Date.now()}`,
        ...processedFieldForm,
      };
      if (editingFieldSectionId) {
        // Crear nuevo campo dentro de la sección especificada
        updatedFields = (editedData.custom_fields || []).map((item) => {
          if (item.id === editingFieldSectionId && item.fields) {
            return {
              ...item,
              fields: [...(item.fields || []), newField],
            };
          }
          return item;
        });
      } else {
        // Crear campo suelto (sin sección)
        updatedFields = [...(editedData.custom_fields || []), newField];
      }
    }

    handleCustomFieldsChange(updatedFields);
    setFieldModalOpen(false);

    return {
      success: true,
      needsSave: true,
      updatedFields,
      action: editingField ? "editado" : "creado",
    };
  };

  /**
   * Elimina un campo personalizado de su sección o como campo suelto.
   *
   * @param {Object} field - Campo a eliminar.
   * @param {string|null} sectionId - ID de la sección que contiene el campo, o null si es suelto.
   * @returns {Object} Objeto con success, needsSave, deletedField y updatedFields.
   */
  const handleDeleteField = (field, sectionId) => {
    let updatedFields;
    if (sectionId) {
      updatedFields = (editedData.custom_fields || []).map((item) => {
        if (item.id === sectionId && item.fields) {
          return {
            ...item,
            fields: item.fields.filter((f) => f.id !== field.id),
          };
        }
        return item;
      });
    } else {
      // Eliminar campo suelto
      updatedFields = (editedData.custom_fields || []).filter((item) => item.id !== field.id);
    }

    handleCustomFieldsChange(updatedFields);

    return {
      success: true,
      needsSave: true,
      deletedField: field.label || field.name,
      updatedFields,
    };
  };

  /**
   * Abre el Dialog de confirmación de eliminación.
   *
   * @param {Object} item - Campo o sección que se desea eliminar.
   * @param {string} type - Tipo de eliminación: "field" o "section".
   * @param {string} [sectionId] - ID de la sección (solo para campos).
   * @returns {void} No retorna valor, actualiza estados y abre modal.
   */
  const handleClickDelete = (item, type = "field", sectionId = null) => {
    setInfoDelete({ ...item, _sectionId: sectionId });
    setDeleteType(type);
    setOpenDelete(true);
  };

  /**
   * Cierra el Dialog de confirmación de eliminación.
   *
   * Este método cierra el modal de confirmación y limpia los datos
   * del campo a eliminar.
   *
   * @returns {void} No retorna valor, actualiza estados.
   */
  const closeDelete = () => {
    setOpenDelete(false);
    setInfoDelete({});
  };

  /**
   * Confirma y ejecuta la eliminación del campo o sección.
   *
   * @param {Event} e - Evento del formulario.
   * @returns {Promise<void>} No retorna valor, actualiza estados y recarga datos.
   */
  const submitDelete = async (e) => {
    e.preventDefault();

    let result;
    if (deleteType === "section") {
      result = handleDeleteSection(infoDelete);
    } else {
      result = handleDeleteField(infoDelete, infoDelete._sectionId);
    }

    if (result.needsSave) {
      // Crear datos temporales con los campos actualizados
      const tempEditedData = {
        ...editedData,
        custom_fields: result.updatedFields,
      };

      // Guardar directamente con los campos actualizados
      setSaving(true);
      try {
        const dataToSend = {
          form_start_date: formatDateForApi(tempEditedData.form_start_date),
          form_end_date: formatDateForApi(tempEditedData.form_end_date),
          card_effective_date: formatDateForApi(tempEditedData.card_effective_date),
          card_expiration_date: formatDateForApi(tempEditedData.card_expiration_date),
          is_active: tempEditedData.is_active,
          max_registrations: tempEditedData.max_registrations
            ? parseInt(tempEditedData.max_registrations)
            : null,
          custom_fields: result.updatedFields, // Usar los campos actualizados directamente
        };

        const submitResult = await updateEventRegistrationForm(dataSourceId, dataToSend);

        if (submitResult.status !== false) {
          setTypeAlert("success");
          setMessage(`Campo "${result.deletedField}" eliminado exitosamente`);
          setIsAlert(true);
          closeDelete();
          // Recargar los datos sin refrescar la página
          await loadFormData();
        } else {
          setTypeAlert("fail");
          setMessage(submitResult.message || "Error al eliminar el campo");
          setIsAlert(true);
        }
      } catch (err) {
        console.error("Error al eliminar campo:", err);
        setTypeAlert("fail");
        setMessage("Error al eliminar el campo");
        setIsAlert(true);
      } finally {
        setSaving(false);
      }
    }
  };

  /**
   * Guarda la configuración general del formulario y recarga datos.
   *
   * Este método es un wrapper que guarda la configuración del formulario,
   * muestra alertas de resultado y recarga los datos actualizados.
   *
   * @returns {Promise<void>} No retorna valor, actualiza estados y recarga datos.
   */
  const handleSaveConfig = async () => {
    const result = await handleSubmit();
    if (result.success) {
      setTypeAlert("success");
      setMessage(result.message);
      setIsAlert(true);
      setConfigModalOpen(false);
      // Recargar los datos sin refrescar la página
      await loadFormData();
    } else {
      setTypeAlert("fail");
      setMessage(result.message);
      setIsAlert(true);
    }
  };

  /**
   * Wrapper para guardar secciones con persistencia.
   *
   * @returns {Promise<void>} No retorna valor, actualiza estados y recarga datos.
   */
  const handleSaveSectionWrapper = async () => {
    const result = handleSaveSection();
    if (!result.success) return;

    if (result.needsSave) {
      setSaving(true);
      try {
        const dataToSend = {
          form_start_date: formatDateForApi(editedData.form_start_date),
          form_end_date: formatDateForApi(editedData.form_end_date),
          card_effective_date: formatDateForApi(editedData.card_effective_date),
          card_expiration_date: formatDateForApi(editedData.card_expiration_date),
          is_active: editedData.is_active,
          max_registrations: editedData.max_registrations
            ? parseInt(editedData.max_registrations)
            : null,
          custom_fields: result.updatedFields,
        };

        const submitResult = await updateEventRegistrationForm(dataSourceId, dataToSend);

        if (submitResult.status !== false) {
          setTypeAlert("success");
          setMessage(`Sección ${result.action} exitosamente`);
          setIsAlert(true);
          await loadFormData();
        } else {
          setTypeAlert("fail");
          setMessage(submitResult.message || "Error al guardar la sección");
          setIsAlert(true);
        }
      } catch (err) {
        console.error("Error al guardar sección:", err);
        setTypeAlert("fail");
        setMessage("Error al guardar la sección");
        setIsAlert(true);
      } finally {
        setSaving(false);
      }
    }
  };

  /**
   * Wrapper para guardar campos personalizados con validación y persistencia.
   *
   * Este método valida el campo, lo guarda en el backend inmediatamente
   * y muestra alertas de resultado, evitando pérdida de datos.
   *
   * @returns {Promise<void>} No retorna valor, actualiza estados y recarga datos.
   */
  const handleSaveFieldWrapper = async () => {
    const result = handleSaveField();
    if (!result.success) {
      setTypeAlert("fail");
      setMessage(result.message);
      setIsAlert(true);
      return;
    }

    if (result.needsSave) {
      // Guardar directamente con los campos actualizados
      setSaving(true);
      try {
        const dataToSend = {
          form_start_date: formatDateForApi(editedData.form_start_date),
          form_end_date: formatDateForApi(editedData.form_end_date),
          card_effective_date: formatDateForApi(editedData.card_effective_date),
          card_expiration_date: formatDateForApi(editedData.card_expiration_date),
          is_active: editedData.is_active,
          max_registrations: editedData.max_registrations
            ? parseInt(editedData.max_registrations)
            : null,
          custom_fields: result.updatedFields, // Usar los campos actualizados directamente
        };

        const submitResult = await updateEventRegistrationForm(dataSourceId, dataToSend);

        if (submitResult.status !== false) {
          setTypeAlert("success");
          setMessage(`Campo ${result.action} exitosamente`);
          setIsAlert(true);
          // Recargar los datos sin refrescar la página
          await loadFormData();
        } else {
          setTypeAlert("fail");
          setMessage(submitResult.message || "Error al guardar el campo");
          setIsAlert(true);
        }
      } catch (err) {
        console.error("Error al guardar campo:", err);
        setTypeAlert("fail");
        setMessage("Error al guardar el campo");
        setIsAlert(true);
      } finally {
        setSaving(false);
      }
    }
  };

  /**
   * Wrapper para copyToClipboard que muestra alertas de éxito.
   *
   * Este método copia el texto al portapapeles y muestra una alerta
   * de confirmación al usuario.
   *
   * @param {string} text - Texto a copiar al portapapeles.
   * @returns {void} No retorna valor, muestra alerta.
   */
  const onCopyToClipboard = (text) => {
    const result = copyToClipboard(text);
    setTypeAlert("success");
    setMessage(result.message);
    setIsAlert(true);
  };

  /**
   * Genera y descarga un código QR con la URL pública del formulario.
   *
   * Crea un código QR en formato PNG utilizando la librería qrcode,
   * y lo descarga automáticamente con el nombre del evento.
   *
   * @returns {void} No retorna valor, descarga el QR o muestra error.
   */
  const handleDownloadQR = () => {
    const publicUrl = getPublicUrl();
    if (!publicUrl) {
      setTypeAlert("error");
      setMessage("No hay URL pública disponible para generar el código QR");
      setIsAlert(true);
      return;
    }

    // Crear un canvas para generar el QR
    const canvas = document.createElement("canvas");
    const size = 512; // Tamaño del QR en píxeles
    canvas.width = size;
    canvas.height = size;

    // Usar la librería QRCode para generar el QR
    import("qrcode")
      .then((QRCode) => {
        QRCode.toCanvas(canvas, publicUrl, {
          width: size,
          margin: 2,
          color: {
            dark: "#000000",
            light: "#ffffff",
          },
        })
          .then(() => {
            // Convertir canvas a blob y descargar
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `QR-${formData?.data_source?.source_name || "Evento"}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              // Mostrar alerta de éxito
              setTypeAlert("success");
              setMessage("Código QR descargado exitosamente");
              setIsAlert(true);
            });
          })
          .catch((err) => {
            console.error("Error al generar QR:", err);
            setTypeAlert("error");
            setMessage("Error al generar el código QR");
            setIsAlert(true);
          });
      })
      .catch((err) => {
        console.error("Error al cargar librería QRCode:", err);
        setTypeAlert("error");
        setMessage("Error al cargar la librería de códigos QR");
        setIsAlert(true);
      });
  };

  // Renderizar componente con modales
  return (
    <>
      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />

      <EventFormDetailPage
        dataSourceId={dataSourceId}
        formData={formData}
        loading={loading}
        copyToClipboard={onCopyToClipboard}
        getPublicUrl={getPublicUrl}
        handleCreateField={handleCreateField}
        handleEditField={handleEditField}
        handleClickDelete={handleClickDelete}
        handleCreateSection={handleCreateSection}
        handleEditSection={handleEditSection}
        setConfigModalOpen={setConfigModalOpen}
        canEdit={canEditEventForm}
        handleDownloadQR={handleDownloadQR}
      />

      <EventConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        editedData={editedData}
        handleFieldChange={handleFieldChange}
        onSave={handleSaveConfig}
        saving={saving}
      />

      <CustomFieldModal
        open={fieldModalOpen}
        onClose={handleCloseFieldModal}
        editingField={editingField}
        fieldForm={fieldForm}
        onFieldChange={handleFieldFormChange}
        onSave={handleSaveFieldWrapper}
        saving={saving}
      />

      <FieldDelete
        openDelete={openDelete}
        closeDelete={closeDelete}
        infoDelete={infoDelete}
        submitDelete={submitDelete}
      />

      <SectionModal
        open={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        editingSection={editingSection}
        sectionName={sectionName}
        onNameChange={setSectionName}
        onSave={handleSaveSectionWrapper}
        saving={saving}
      />
    </>
  );
}

/**
 * Hook personalizado para obtener solo el nombre del evento.
 *
 * Este hook se utiliza en componentes que necesitan mostrar el nombre
 * del evento sin cargar toda la configuración del formulario.
 *
 * @returns {Object} Objeto con eventName (string) y loading (boolean).
 */
export function useEventName() {
  const { dataSourceId } = useParams();
  const [eventName, setEventName] = useState("Formulario de Evento");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEventName = async () => {
      if (!dataSourceId) return;
      try {
        const result = await getEventFormByDataSource(dataSourceId);
        if (result.status === true && result.data) {
          setEventName(result.data?.data_source?.source_name || "Formulario de Evento");
        }
      } catch (error) {
        console.error("Error al cargar nombre del evento:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEventName();
  }, [dataSourceId]);

  return { eventName, loading };
}
